import { useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ChatPanel({ requestId, currentUserRole }) {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/quote-requests/${requestId}/messages`);
      setMessages(data);
    } catch (err) {
      console.warn("[chat] failed to load messages", err?.message || err);
    }
    finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 12000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [requestId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/quote-requests/${requestId}/messages`, {
        body: body.trim(),
      });
      setMessages((m) => [...m, data]);
      setBody("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-5" data-testid={`chat-${requestId}`}>
      <div className="av-eyebrow mb-2">Conversazione con {currentUserRole === "admin" ? "il cliente" : "NMA"}</div>
      <div
        ref={scrollRef}
        className="av-card p-4 max-h-64 overflow-y-auto space-y-3"
        style={{ background: "rgba(20,18,16,0.6)" }}
        data-testid={`chat-messages-${requestId}`}
      >
        {loading ? (
          <p className="text-[#A39E93] text-sm">Caricamento…</p>
        ) : messages.length === 0 ? (
          <p className="text-[#A39E93] text-sm">
            Nessun messaggio. Scrivi qui per raffinare i dettagli del preventivo.
          </p>
        ) : (
          messages.map((m) => {
            const mine = m.sender_role === currentUserRole;
            const isAdmin = m.sender_role === "admin";
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${
                    isAdmin
                      ? "bg-[#E5A93C]/15 border border-[#E5A93C]/40 text-[#F7F4EE]"
                      : "bg-[#F7F4EE]/10 border av-hairline text-[#F7F4EE]"
                  }`}
                  data-testid={`chat-msg-${m.id}`}
                >
                  <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1">
                    {isAdmin ? "NMA" : (m.sender_name || "Cliente")}
                    {" · "}
                    {new Date(m.created_at).toLocaleString("it-IT", {
                      day: "2-digit",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                  <div className="whitespace-pre-line">{m.body}</div>
                </div>
              </div>
            );
          })
        )}
      </div>
      <form onSubmit={send} className="flex gap-2 mt-3">
        <input
          className="av-input flex-1"
          placeholder="Scrivi un messaggio…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          data-testid={`chat-input-${requestId}`}
        />
        <button
          type="submit"
          disabled={sending || !body.trim()}
          className="av-pill av-pill-gold text-xs"
          data-testid={`chat-send-${requestId}`}
        >
          <Send size={12} /> {sending ? "…" : "Invia"}
        </button>
      </form>
    </div>
  );
}
